import { Autocomplete, TextField, TextFieldProps } from '@mui/material'
import { MapPinPlusIcon } from 'lucide-react'
import { ChangeEvent, FC } from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from 'use-places-autocomplete'

type FormFieldCheckboxProps = Omit<UseControllerProps, 'control'> &
    Omit<TextFieldProps, 'name' | 'value' | 'ref'> & {
        setOffice: (position: google.maps.LatLngLiteral) => void
    }

export const FormFieldAutoCompleteMap: FC<FormFieldCheckboxProps> = ({
    name,
    rules,
    disabled: disableMap,
    setOffice,
    variant = 'filled',
    ...rest
}) => {
    const { control, setValue: setFormValue } = useFormContext()
    const disabled = disableMap || (control as any)._options?.disabled

    const {
        value,
        setValue,
        suggestions: { data },
    } = usePlacesAutocomplete({ debounce: 300 })

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const { value: address } = event.target

        if (address.trim().length > 2) {
            setValue(address)
            setFormValue(name, address)
        }

        if (address.trim().length >= 10) {
            getGeocodeByAddress(address).then(
                position => !!position && setOffice(position),
            )
        }
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={value}
            render={({
                field: { onChange, value, ...field },
                fieldState: { error },
            }) => (
                <Autocomplete
                    {...field}
                    value={value}
                    freeSolo
                    includeInputInList
                    filterSelectedOptions
                    onChange={(_, address) => {
                        onChange(address)
                        getGeocodeByAddress(address).then(
                            position => !!position && setOffice(position),
                        )
                    }}
                    readOnly={disabled}
                    renderInput={autoCompleteProps => (
                        <TextField
                            label="endereço"
                            {...autoCompleteProps}
                            {...rest}
                            disabled={disabled}
                            variant={variant}
                            size="medium"
                            fullWidth
                            onChange={handleChange}
                            error={!!error}
                            helperText={error ? error.message : null}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <MapPinPlusIcon className="mr-1 shrink-0 text-error" />
                            {option}
                        </li>
                    )}
                    options={data.map(local => local.description)}
                />
            )}
        />
    )
}

async function getGeocodeByAddress(address: string) {
    try {
        const results = await getGeocode({ address })
        return await getLatLng(results[0])
    } catch (_error) {
        //
    }
}
